import { useEffect, useState, useCallback, useRef } from 'react'

// App background color (matching Bottom Nav and main app background)
const APP_BACKGROUND_COLOR = '#1e2832'

export interface TelegramUser {
  id: number
  first_name: string
  last_name?: string
  username?: string
  language_code?: string
  is_premium?: boolean
}

export const useTelegram = () => {
  const [webApp, setWebApp] = useState<TelegramWebApp | null>(null)
  const [user, setUser] = useState<TelegramUser | null>(null)
  const [isReady, setIsReady] = useState(false)
  const backButtonHandlerRef = useRef<(() => void) | null>(null)
  const mainButtonHandlerRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    const tg = window.Telegram?.WebApp
    console.log('[useTelegram] Telegram WebApp:', tg)
    console.log('[useTelegram] initDataUnsafe:', tg?.initDataUnsafe)

    if (tg) {
      setWebApp(tg)
      const telegramUser = tg.initDataUnsafe?.user || null
      console.log('[useTelegram] Telegram user:', telegramUser)
      setUser(telegramUser)

      // Initialize Telegram WebApp
      tg.ready()
      tg.expand()

      // Only enable closing confirmation if supported
      try {
        if (tg.isClosingConfirmationEnabled !== undefined) {
          tg.enableClosingConfirmation()
        }
      } catch (e) {
        console.log('Closing confirmation not supported')
      }

      setIsReady(true)
    } else {
      // For testing in browser without Telegram
      console.log('[useTelegram] No Telegram WebApp found - running outside Telegram')
      setIsReady(true)
    }
  }, [])

  const showAlert = useCallback((message: string) => {
    webApp?.showAlert(message)
  }, [webApp])

  const showConfirm = useCallback((message: string): Promise<boolean> => {
    return new Promise((resolve) => {
      webApp?.showConfirm(message, (confirmed: boolean) => resolve(confirmed))
    })
  }, [webApp])

  const showPopup = useCallback((params: {
    title?: string
    message: string
    buttons?: Array<{
      id?: string
      type?: 'default' | 'ok' | 'close' | 'cancel' | 'destructive'
      text?: string
    }>
  }): Promise<string> => {
    return new Promise((resolve) => {
      webApp?.showPopup(params, (buttonId: string) => resolve(buttonId || ''))
    })
  }, [webApp])

  const hapticFeedback = useCallback((style: 'light' | 'medium' | 'heavy' = 'medium') => {
    try {
      webApp?.HapticFeedback?.impactOccurred(style)
    } catch (e) {
      // Haptic not supported, silently ignore
    }
  }, [webApp])

  const hapticNotification = useCallback((type: 'error' | 'success' | 'warning') => {
    try {
      webApp?.HapticFeedback?.notificationOccurred(type)
    } catch (e) {
      // Haptic not supported, silently ignore
    }
  }, [webApp])

  const close = useCallback(() => {
    webApp?.close()
  }, [webApp])

  const openLink = useCallback((url: string) => {
    webApp?.openLink(url)
  }, [webApp])

  const openTelegramLink = useCallback((url: string) => {
    webApp?.openTelegramLink(url)
  }, [webApp])

  // MainButton controls
  const showMainButton = useCallback((text: string, onClick: () => void, color?: string, textColor?: string) => {
    if (!webApp?.MainButton) return

    // Remove previous handler if exists
    if (mainButtonHandlerRef.current) {
      webApp.MainButton.offClick(mainButtonHandlerRef.current)
    }

    webApp.MainButton.setText(text)
    if (color) webApp.MainButton.color = color
    if (textColor) webApp.MainButton.textColor = textColor

    // Set bottom bar color to match app background
    try {
      if (webApp.setBottomBarColor) {
        webApp.setBottomBarColor(APP_BACKGROUND_COLOR)
      }
    } catch (e) {
      console.log('setBottomBarColor not supported')
    }

    // Store and add new handler
    mainButtonHandlerRef.current = onClick
    webApp.MainButton.onClick(onClick)
    webApp.MainButton.show()
    webApp.MainButton.enable()
  }, [webApp])

  const hideMainButton = useCallback(() => {
    if (!webApp?.MainButton) return

    // Remove handler if exists
    if (mainButtonHandlerRef.current) {
      webApp.MainButton.offClick(mainButtonHandlerRef.current)
      mainButtonHandlerRef.current = null
    }

    // Reset bottom bar color when hiding button
    try {
      if (webApp.setBottomBarColor) {
        webApp.setBottomBarColor(APP_BACKGROUND_COLOR)
      }
    } catch (e) {
      console.log('setBottomBarColor not supported')
    }

    webApp.MainButton.hide()
  }, [webApp])

  const setMainButtonLoading = useCallback((loading: boolean) => {
    if (!webApp?.MainButton) return

    if (loading) {
      webApp.MainButton.showProgress()
      webApp.MainButton.disable()
    } else {
      webApp.MainButton.hideProgress()
      webApp.MainButton.enable()
    }
  }, [webApp])

  const setMainButtonText = useCallback((text: string) => {
    webApp?.MainButton?.setText(text)
  }, [webApp])

  // Configure MainButton with all available options
  const configureMainButton = useCallback((options: {
    text?: string
    color?: string
    textColor?: string
    isVisible?: boolean
    isActive?: boolean
    isProgressVisible?: boolean
    hasShineEffect?: boolean
  }) => {
    if (!webApp?.MainButton) return

    if (options.text !== undefined) webApp.MainButton.setText(options.text)

    if (options.color !== undefined) {
      webApp.MainButton.color = options.color
    }

    if (options.textColor !== undefined) {
      webApp.MainButton.textColor = options.textColor
    }

    if (options.isVisible !== undefined) {
      if (options.isVisible) {
        webApp.MainButton.show()
      } else {
        webApp.MainButton.hide()
      }
    }

    if (options.isActive !== undefined) {
      if (options.isActive) {
        webApp.MainButton.enable()
      } else {
        webApp.MainButton.disable()
      }
    }

    if (options.isProgressVisible !== undefined) {
      if (options.isProgressVisible) {
        webApp.MainButton.showProgress()
      } else {
        webApp.MainButton.hideProgress()
      }
    }

    // hasShineEffect is only available in newer versions
    if (options.hasShineEffect !== undefined && 'hasShineEffect' in webApp.MainButton) {
      try {
        ;(webApp.MainButton as any).hasShineEffect = options.hasShineEffect
      } catch (e) {
        console.log('hasShineEffect not supported')
      }
    }
  }, [webApp])

  // BackButton controls
  const showBackButton = useCallback((onClick: () => void) => {
    if (!webApp?.BackButton) return

    // Remove previous handler if exists
    if (backButtonHandlerRef.current) {
      webApp.BackButton.offClick(backButtonHandlerRef.current)
    }

    // Store and add new handler
    backButtonHandlerRef.current = onClick
    webApp.BackButton.onClick(onClick)
    webApp.BackButton.show()
  }, [webApp])

  const hideBackButton = useCallback(() => {
    if (!webApp?.BackButton) return

    // Remove handler if exists
    if (backButtonHandlerRef.current) {
      webApp.BackButton.offClick(backButtonHandlerRef.current)
      backButtonHandlerRef.current = null
    }

    webApp.BackButton.hide()
  }, [webApp])

  // Bottom bar color controls
  const setBottomBarColor = useCallback((color: string) => {
    try {
      if (webApp?.setBottomBarColor) {
        webApp.setBottomBarColor(color)
      }
    } catch (e) {
      console.log('setBottomBarColor not supported')
    }
  }, [webApp])

  const resetBottomBarColor = useCallback(() => {
    try {
      if (webApp?.setBottomBarColor) {
        webApp.setBottomBarColor(APP_BACKGROUND_COLOR)
      }
    } catch (e) {
      console.log('setBottomBarColor not supported')
    }
  }, [webApp])

  return {
    webApp,
    user,
    isReady,
    telegramId: user?.id || (typeof localStorage !== 'undefined' ? parseInt(localStorage.getItem('test_telegram_id') || '0', 10) || undefined : undefined),
    colorScheme: webApp?.colorScheme || 'light',
    platform: webApp?.platform || 'unknown',
    showAlert,
    showConfirm,
    showPopup,
    hapticFeedback,
    hapticNotification,
    close,
    openLink,
    openTelegramLink,
    showMainButton,
    hideMainButton,
    setMainButtonLoading,
    setMainButtonText,
    configureMainButton,
    showBackButton,
    hideBackButton,
    setBottomBarColor,
    resetBottomBarColor,
  }
}
